/**
 * BoyceRunAllCard
 * -----------------------------------------------------------------------------
 * One-shot uploader for all six Boyce CSVs. Posts to /run-all which kicks off
 * a background job, then polls /run-all/{job_id} every 2 s until completion.
 *
 * UI shows:
 *   - 6 file pickers (with filename hints)
 *   - "Start Import" button (disabled until all 6 files chosen)
 *   - live progress: which step is running, per-step counts, final summary
 *
 * Browser never blocks — the server reads files in one request and runs
 * everything in a background coroutine.
 */
import { useEffect, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Rocket, FileText, Loader2, CheckCircle2, AlertTriangle, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { key: 'customers_file',           label: 'HoppersCustomers.csv',         hint: 'Customer master' },
  { key: 'service_customers_file',   label: 'HoppersServiceCustomers.csv',  hint: 'Service-customer history' },
  { key: 'vehicles_file',            label: 'HoppersServiceVehicles.csv',   hint: 'Vehicle master' },
  { key: 'service_file',             label: 'HoppersService.csv',           hint: 'Service records' },
  { key: 'invoice_header_file',      label: 'HoppersInvoiceHeader.csv',     hint: 'Invoice headers' },
  { key: 'invoice_lines_file',       label: 'HoppersLineItems.csv',         hint: 'Invoice line items' },
];

const STEP_LABELS = {
  customers: 'Customers',
  service_customers: 'Service Customers',
  vehicles: 'Vehicles',
  invoices: 'Invoices',
  cleanup: 'Auto-Cleanup',
  done: 'Done',
};

export default function BoyceRunAllCard({ onComplete }) {
  const [files, setFiles] = useState({});
  const [running, setRunning] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobState, setJobState] = useState(null);
  const pollRef = useRef(null);

  const allChosen = FIELDS.every(f => files[f.key]);

  const setFile = (key, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error(`${file.name} is not a .csv file`);
      return;
    }
    setFiles(prev => ({ ...prev, [key]: file }));
  };

  const start = async () => {
    if (!allChosen) { toast.error('Please choose all 6 files'); return; }
    setRunning(true);
    setJobState(null);
    try {
      const form = new FormData();
      FIELDS.forEach(f => form.append(f.key, files[f.key]));
      const res = await fetch(`${BACKEND_URL}/api/super-admin/boyce-import/run-all`, {
        method: 'POST', credentials: 'include', body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Failed: HTTP ${res.status}`);
        setRunning(false);
        return;
      }
      const data = await res.json();
      setJobId(data.job_id);
      toast.success(`Import started · job ${data.job_id}`);
      // Begin polling
      pollRef.current = setInterval(() => pollOnce(data.job_id), 2500);
    } catch (e) {
      toast.error(`Failed to start: ${e.message || e}`);
      setRunning(false);
    }
  };

  const pollOnce = async (id) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/super-admin/boyce-import/run-all/${id}`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const j = await res.json();
      setJobState(j);
      if (j.status === 'completed' || j.status === 'failed') {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        setRunning(false);
        if (j.status === 'completed') {
          toast.success('Import completed!');
          onComplete?.(j);
        } else {
          toast.error(`Import failed: ${j.error || 'unknown error'}`);
        }
      }
    } catch (_e) { /* keep polling */ }
  };

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  return (
    <Card className="p-6 border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white">
      <h3 className="font-bold text-slate-900 flex items-center gap-2 text-lg">
        <Rocket className="h-5 w-5 text-emerald-600" />
        Quick Start · Upload All CSVs at Once
      </h3>
      <p className="text-sm text-slate-600 mt-1 max-w-3xl">
        Drag-and-drop all six Boyce CSVs below, then click <strong>Start Import</strong>. The server runs every step
        (Customers → Service Customers → Vehicles → Invoices → Auto-Cleanup) as a background job and the UI shows live progress —
        your browser never freezes, no matter how large the files.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        {FIELDS.map(f => {
          const chosen = files[f.key];
          return (
            <div key={f.key} className="border border-slate-200 rounded-lg p-3 bg-white">
              <p className="text-xs font-semibold text-slate-700">{f.label}</p>
              <p className="text-[10px] text-slate-500">{f.hint}</p>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => setFile(f.key, e.target.files?.[0])}
                disabled={running}
                className="mt-2 text-xs h-8"
                data-testid={`runall-input-${f.key}`}
              />
              {chosen && (
                <p className="text-[10px] text-emerald-700 mt-1 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> {chosen.name} · {(chosen.size / 1024 / 1024).toFixed(1)} MB
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <Button
          onClick={start}
          disabled={!allChosen || running}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="runall-start"
        >
          {running ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Running…</> : <><Rocket className="h-4 w-4 mr-2" />Start Import</>}
        </Button>
        {jobId && <Badge variant="secondary">Job: {jobId}</Badge>}
        {jobState && (
          <Badge variant="outline" className={
            jobState.status === 'completed' ? 'border-emerald-400 text-emerald-700'
            : jobState.status === 'failed' ? 'border-rose-400 text-rose-700'
            : 'border-blue-400 text-blue-700'
          }>
            {jobState.status === 'completed' ? '✓ Completed'
              : jobState.status === 'failed' ? '✗ Failed'
              : `Step: ${STEP_LABELS[jobState.current_step] || jobState.current_step}`}
          </Badge>
        )}
      </div>

      {/* Progress card */}
      {jobState && (
        <div className="mt-4 space-y-2" data-testid="runall-progress">
          {['customers', 'service_customers', 'vehicles', 'invoices', 'cleanup'].map(stepKey => {
            const stepData = jobState.progress?.[stepKey];
            const isCurrent = jobState.current_step === stepKey;
            const isDone = !!stepData;
            return (
              <div
                key={stepKey}
                className={`border rounded-md px-3 py-2 flex items-center justify-between ${
                  isCurrent ? 'border-blue-300 bg-blue-50/50' : isDone ? 'border-emerald-300 bg-emerald-50/30' : 'border-slate-200 bg-white'
                }`}
                data-testid={`runall-step-${stepKey}`}
              >
                <div className="flex items-center gap-2">
                  {isDone ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    : isCurrent ? <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    : <FileText className="h-4 w-4 text-slate-400" />}
                  <span className="text-sm font-medium text-slate-800">{STEP_LABELS[stepKey]}</span>
                </div>
                {isDone && stepData && (
                  <span className="text-[10px] text-slate-500 max-w-md text-right truncate">
                    {Object.entries(stepData).slice(0, 4).map(([k, v]) => `${k}=${typeof v === 'object' ? '…' : v}`).join(' · ')}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {jobState?.status === 'completed' && jobState.progress?.cleanup && (
        <div className="mt-4 border-2 border-emerald-300 rounded-lg p-4 bg-white" data-testid="runall-summary">
          <p className="font-semibold text-emerald-700 flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> Import & Auto-Cleanup Complete
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2 text-xs">
            <div><span className="text-slate-500">Phones normalised:</span> <strong>{jobState.progress.cleanup.phone_fields_normalised?.toLocaleString()}</strong></div>
            <div><span className="text-slate-500">Customers renamed:</span> <strong>{jobState.progress.cleanup.customers_renamed?.toLocaleString()}</strong></div>
            <div><span className="text-slate-500">Invoices reconciled:</span> <strong>{jobState.progress.cleanup.invoice_customer_names_reconciled?.toLocaleString()}</strong></div>
            <div className={jobState.progress.cleanup.remaining_unknown_customers === 0 ? 'text-emerald-700' : 'text-amber-700'}>
              <span className="text-slate-500">Still unnamed:</span> <strong>{jobState.progress.cleanup.remaining_unknown_customers?.toLocaleString()}</strong>
            </div>
          </div>
        </div>
      )}

      {jobState?.status === 'failed' && (
        <div className="mt-4 border-2 border-rose-300 rounded-lg p-4 bg-rose-50 text-rose-800 text-sm" data-testid="runall-error">
          <p className="font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Import failed</p>
          <p className="mt-1 text-xs">{jobState.error}</p>
          <p className="mt-2 text-xs text-rose-700">Each step is idempotent — you can fix the failing file and click <strong>Start Import</strong> again.</p>
        </div>
      )}
    </Card>
  );
}
